import React from 'react';
import {
  CalendarOutlined,
  CheckCircleOutlined,
  FolderOpenOutlined,
  NumberOutlined,
} from '@ant-design/icons';
import { useIntl } from 'react-intl';
import { Spin } from 'antd';
import { Locale } from '../../context/AppProvider';
import { usePolicyClaimsHook } from '../../data/policyClaims/usePolicyClaimsHook';
import { useEscDossierContext } from '../DossierContext';
import {
  PanelDot,
  PanelHead,
  PanelLead,
  PanelList,
  PanelPage,
  PanelPill,
  PanelRow,
} from '../PanelList/PanelList';
import styles from '../PanelList/PanelList.module.scss';

interface Props {
  dossierId: string;
}

const CLOSED_STATUSES = new Set(['Closed', 'Denied']);

const ClaimsTab: React.FC<Props> = ({ dossierId }) => {
  const { messages } = React.useContext(Locale);
  const intl = useIntl();
  const emptyMsg = messages['esc.claims.empty'] ?? 'No claims yet';
  const claimsLabel = messages['esc.tabs.claims'] ?? 'Claims';
  const { onClaimClick } = useEscDossierContext();

  const [{ claims, hasData, status }, claimsHandler] =
    usePolicyClaimsHook(dossierId);
  const dispatchedRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!dossierId || hasData || dispatchedRef.current === dossierId) {
      return;
    }
    dispatchedRef.current = dossierId;
    claimsHandler.fetchClaims();
  }, [dossierId, hasData, claimsHandler]);

  const fmtDate = (d: string) =>
    intl.formatDate(d, { month: 'short', day: 'numeric', year: 'numeric' });

  if (!hasData) {
    if (status === 'rejected') {
      return <div className={styles.empty}>{emptyMsg}</div>;
    }
    return (
      <div className={styles.loading}>
        <Spin />
      </div>
    );
  }

  if (claims.length === 0) {
    return <div className={styles.empty}>{emptyMsg}</div>;
  }

  return (
    <PanelPage>
      <PanelHead
        icon={<FolderOpenOutlined />}
        title={claimsLabel}
        count={claims.length}
      />
      <PanelList>
        {claims.map((c) => {
          const closed = CLOSED_STATUSES.has(c.status);
          const metaParts: React.ReactNode[] = [];
          if (c.number && c.caption) {
            metaParts.push(
              <>
                <NumberOutlined />
                {c.number}
              </>
            );
          }
          if (c.created_at) {
            metaParts.push(
              <>
                <CalendarOutlined />
                {fmtDate(c.created_at)}
              </>
            );
          }
          if (c.closed_at) {
            metaParts.push(
              <>
                <CheckCircleOutlined />
                {fmtDate(c.closed_at)}
              </>
            );
          }
          return (
            <PanelRow
              key={c.claim_id}
              clickable
              onClick={() => onClaimClick(c.claim_id)}
              lead={
                <PanelLead>
                  <FolderOpenOutlined />
                </PanelLead>
              }
              title={c.caption || c.number || c.claim_id}
              meta={
                metaParts.length > 0
                  ? metaParts.map((part, i) => (
                      <React.Fragment key={i}>
                        {i > 0 && <PanelDot />}
                        {part}
                      </React.Fragment>
                    ))
                  : undefined
              }
              trail={
                <PanelPill tone={closed ? 'resolved' : 'open'}>
                  {c.status}
                </PanelPill>
              }
            />
          );
        })}
      </PanelList>
    </PanelPage>
  );
};

export default ClaimsTab;
